import { api } from '../client';

export type EquipmentCategory =
  | 'DRILLING'
  | 'COMPLETION'
  | 'PRODUCTION'
  | 'TESTING'
  | 'SAFETY'
  | 'TRANSPORTATION'
  | 'OTHER';

export type EquipmentStatus =
  | 'AVAILABLE'
  | 'IN_USE'
  | 'MAINTENANCE'
  | 'REPAIR'
  | 'RETIRED'
  | 'DISPOSED';

export type EquipmentCondition =
  | 'EXCELLENT'
  | 'GOOD'
  | 'FAIR'
  | 'POOR'
  | 'CRITICAL';

export type DowntimeReason =
  | 'SCHEDULED_MAINTENANCE'
  | 'UNSCHEDULED_MAINTENANCE'
  | 'BREAKDOWN'
  | 'INSPECTION'
  | 'CALIBRATION'
  | 'OPERATOR_ERROR'
  | 'ENVIRONMENTAL'
  | 'OTHER';

export interface Equipment {
  id: string;
  companyId: string;
  assetNumber: string;
  name: string;
  description: string | null;
  category: EquipmentCategory;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  status: EquipmentStatus;
  condition: EquipmentCondition;
  purchaseDate: string | null;
  purchasePrice: number | null;
  warrantyExpiry: string | null;
  lastServiceDate: string | null;
  nextServiceDate: string | null;
  operatingHours: number | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    workOrders: number;
    downtimeEvents: number;
    maintenanceSchedules: number;
  };
}

export interface EquipmentDetail extends Equipment {
  workOrders: {
    id: string;
    workOrderNumber: string;
    title: string;
    type: string;
    status: string;
    scheduledStart: string | null;
    actualEnd: string | null;
  }[];
  downtimeEvents: DowntimeEvent[];
  maintenanceSchedules: EquipmentMaintenanceSchedule[];
  certificates: {
    id: string;
    certificate: {
      id: string;
      certificateNumber: string;
      type: string;
      name: string;
      expiryDate: string | null;
    };
  }[];
}

export interface DowntimeEvent {
  id: string;
  equipmentId: string;
  startTime: string;
  endTime: string | null;
  reason: DowntimeReason;
  description: string | null;
  workOrderId: string | null;
  workOrder?: {
    id: string;
    workOrderNumber: string;
    title: string;
  };
  reportedById: string;
  reportedBy: {
    id: string;
    fullName: string;
  };
  cost: number | null;
  createdAt: string;
  updatedAt: string;
}

interface EquipmentMaintenanceSchedule {
  id: string;
  name: string;
  frequency: string;
  nextDue: string;
  priority: string;
  status: string;
}

export interface CreateEquipmentDto {
  assetNumber: string;
  name: string;
  description?: string;
  category: EquipmentCategory;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  locationId: string;
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  lastServiceDate?: string;
  nextServiceDate?: string;
  operatingHours?: number;
}

export interface UpdateEquipmentDto extends Partial<CreateEquipmentDto> {}

export interface RecordDowntimeDto {
  startTime: string;
  endTime?: string;
  reason: DowntimeReason;
  description?: string;
  workOrderId?: string;
  cost?: number;
}

export interface EndDowntimeDto {
  endTime?: string;
  cost?: number;
}

export interface QueryEquipmentParams {
  category?: EquipmentCategory;
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  locationId?: string;
  search?: string;
  maintenanceDue?: boolean;
  page?: number;
  limit?: number;
}

export interface EquipmentDashboard {
  total: number;
  byStatus: Record<EquipmentStatus, number>;
  byCondition: Record<EquipmentCondition, number>;
  maintenanceDue: number;
  utilizationRate: number;
  recentDowntime: {
    id: string;
    startTime: string;
    endTime: string | null;
    reason: DowntimeReason;
    equipment: {
      name: string;
      assetNumber: string;
    };
  }[];
}

export interface EquipmentAnalytics {
  mttr: number;
  totalDowntimeEvents: number;
  topDowntime: {
    equipmentId: string;
    name: string;
    hours: number;
    count: number;
  }[];
  byReason: Record<DowntimeReason, number>;
}

interface EquipmentPaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const equipmentApi = {
  getAll: (params?: QueryEquipmentParams) =>
    api.get<EquipmentPaginatedResponse<Equipment>>('/api/equipment', { params }),

  getById: (id: string) => api.get<EquipmentDetail>(`/api/equipment/${id}`),

  getDashboard: () => api.get<EquipmentDashboard>('/api/equipment/dashboard'),

  getAnalytics: () => api.get<EquipmentAnalytics>('/api/equipment/analytics'),

  create: (data: CreateEquipmentDto) => api.post<Equipment>('/api/equipment', data),

  update: (id: string, data: UpdateEquipmentDto) =>
    api.patch<Equipment>(`/api/equipment/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/api/equipment/${id}`),

  // Downtime management
  getDowntimeHistory: (equipmentId: string) =>
    api.get<DowntimeEvent[]>(`/api/equipment/${equipmentId}/downtime`),

  recordDowntime: (equipmentId: string, data: RecordDowntimeDto) =>
    api.post<DowntimeEvent>(`/api/equipment/${equipmentId}/downtime`, data),

  endDowntime: (equipmentId: string, eventId: string, data: EndDowntimeDto) =>
    api.patch<DowntimeEvent>(`/api/equipment/${equipmentId}/downtime/${eventId}`, data),
};
